import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const emptyFormData = {
  title: { fr: "", en: "", ar: "", nl: "", es: "" },
  description: { fr: "", en: "", ar: "", nl: "", es: "" },
  type: "4x4",
  duration_days: 3,
  price: 0,
  difficulty: "moderate",
  max_group_size: 10,
  departure_city: "Marrakech",
  image_url: "",
  gallery: [],
  route_coordinates: [],
  includes: { fr: [], en: [] },
  highlights: { fr: [], en: [] },
  itinerary: [],
};

const AdminCircuits = () => {
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [activeTab, setActiveTab] = useState("basic");
  
  // Text inputs for list fields
  const [includesFr, setIncludesFr] = useState("");
  const [includesEn, setIncludesEn] = useState("");
  const [highlightsFr, setHighlightsFr] = useState("");
  const [highlightsEn, setHighlightsEn] = useState("");
  const [galleryInput, setGalleryInput] = useState("");

  useEffect(() => {
    fetchCircuits();
  }, []);

  const fetchCircuits = async () => {
    try {
      const response = await axios.get(`${API}/admin/circuits`, getAuthHeaders());
      setCircuits(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse list fields
      const submitData = {
        ...formData,
        includes: {
          fr: includesFr.split("\n").filter(l => l.trim()),
          en: includesEn.split("\n").filter(l => l.trim()) || includesFr.split("\n").filter(l => l.trim()),
        },
        highlights: {
          fr: highlightsFr.split("\n").filter(l => l.trim()),
          en: highlightsEn.split("\n").filter(l => l.trim()) || highlightsFr.split("\n").filter(l => l.trim()),
        },
        gallery: galleryInput.split("\n").filter(l => l.trim()),
      };

      if (editingCircuit) {
        await axios.put(`${API}/admin/circuits/${editingCircuit.id}`, submitData, getAuthHeaders());
        toast.success("Circuit mis à jour");
      } else {
        await axios.post(`${API}/admin/circuits`, submitData, getAuthHeaders());
        toast.success("Circuit créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchCircuits();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (circuit) => {
    setEditingCircuit(circuit);
    setFormData({
      ...emptyFormData,
      ...circuit,
      title: { ...emptyFormData.title, ...circuit.title },
      description: { ...emptyFormData.description, ...circuit.description },
      includes: circuit.includes || { fr: [], en: [] },
      highlights: circuit.highlights || { fr: [], en: [] },
      itinerary: circuit.itinerary || [],
    });
    
    // Set list fields as text
    setIncludesFr(circuit.includes?.fr?.join("\n") || "");
    setIncludesEn(circuit.includes?.en?.join("\n") || "");
    setHighlightsFr(circuit.highlights?.fr?.join("\n") || "");
    setHighlightsEn(circuit.highlights?.en?.join("\n") || "");
    setGalleryInput(circuit.gallery?.join("\n") || "");
    
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce circuit ?")) return;
    try {
      await axios.delete(`${API}/admin/circuits/${id}`, getAuthHeaders());
      toast.success("Circuit supprimé");
      fetchCircuits();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleActive = async (circuit) => {
    try {
      await axios.put(`${API}/admin/circuits/${circuit.id}`, { is_active: !circuit.is_active }, getAuthHeaders());
      toast.success(circuit.is_active ? "Circuit désactivé" : "Circuit activé");
      fetchCircuits();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditingCircuit(null);
    setFormData(emptyFormData);
    setIncludesFr("");
    setIncludesEn("");
    setHighlightsFr("");
    setHighlightsEn("");
    setGalleryInput("");
    setActiveTab("basic");
  };

  // Itinerary management
  const addItineraryDay = () => {
    setFormData({
      ...formData,
      itinerary: [
        ...formData.itinerary,
        {
          day: formData.itinerary.length + 1,
          title: { fr: "", en: "" },
          description: { fr: "", en: "" }
        }
      ]
    });
  };

  const updateItineraryDay = (index, field, lang, value) => {
    const newItinerary = [...formData.itinerary];
    if (!newItinerary[index][field]) {
      newItinerary[index][field] = { fr: "", en: "" };
    }
    newItinerary[index][field][lang] = value;
    setFormData({ ...formData, itinerary: newItinerary });
  };

  const removeItineraryDay = (index) => {
    const newItinerary = formData.itinerary.filter((_, i) => i !== index);
    // Reorder days
    newItinerary.forEach((day, i) => { day.day = i + 1; });
    setFormData({ ...formData, itinerary: newItinerary });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-circuits">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold">Circuits</h1>
          <p className="text-muted-foreground">Gérez vos circuits 4x4 et trekking</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-circuit-btn">
              <Plus className="h-4 w-4" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCircuit ? "Modifier le circuit" : "Nouveau circuit"}</DialogTitle>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="basic">Infos de base</TabsTrigger>
                <TabsTrigger value="content">Contenu</TabsTrigger>
                <TabsTrigger value="itinerary">Itinéraire</TabsTrigger>
                <TabsTrigger value="media">Médias</TabsTrigger>
                <TabsTrigger value="translations">Traductions</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="mt-4">
                {/* TAB: Infos de base */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Titre (FR) *</Label>
                      <Input
                        value={formData.title.fr}
                        onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Titre (EN)</Label>
                      <Input
                        value={formData.title.en}
                        onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Description (FR) *</Label>
                      <Textarea
                        value={formData.description.fr}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })}
                        rows={4}
                        required
                      />
                    </div>
                    <div>
                      <Label>Description (EN)</Label>
                      <Textarea
                        value={formData.description.en}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                        rows={4}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Type *</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4x4">4x4</SelectItem>
                          <SelectItem value="trekking">Trekking</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Difficulté *</Label>
                      <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="easy">Facile</SelectItem>
                          <SelectItem value="moderate">Modéré</SelectItem>
                          <SelectItem value="challenging">Difficile / Sportif</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Durée (jours) *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.duration_days}
                        onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 1 })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Prix (€) *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Groupe max *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.max_group_size}
                        onChange={(e) => setFormData({ ...formData, max_group_size: parseInt(e.target.value) || 1 })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Ville de départ *</Label>
                      <Select 
                        value={formData.departure_city} 
                        onValueChange={(v) => setFormData({ ...formData, departure_city: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Marrakech">Marrakech</SelectItem>
                          <SelectItem value="Ouarzazate">Ouarzazate</SelectItem>
                          <SelectItem value="Agadir">Agadir</SelectItem>
                          <SelectItem value="Zagora">Zagora</SelectItem>
                          <SelectItem value="M'hamid">M'hamid</SelectItem>
                          <SelectItem value="Fès">Fès</SelectItem>
                          <SelectItem value="Casablanca">Casablanca</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* TAB: Contenu */}
                <TabsContent value="content" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Ce qui est inclus (FR) - un élément par ligne</Label>
                      <Textarea
                        value={includesFr}
                        onChange={(e) => setIncludesFr(e.target.value)}
                        rows={6}
                        placeholder="Transport en 4x4&#10;Hébergement&#10;Repas&#10;Guide"
                      />
                    </div>
                    <div>
                      <Label>Ce qui est inclus (EN) - un élément par ligne</Label>
                      <Textarea
                        value={includesEn}
                        onChange={(e) => setIncludesEn(e.target.value)}
                        rows={6}
                        placeholder="4x4 transport&#10;Accommodation&#10;Meals&#10;Guide"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Points forts (FR) - un élément par ligne</Label>
                      <Textarea
                        value={highlightsFr}
                        onChange={(e) => setHighlightsFr(e.target.value)}
                        rows={6}
                        placeholder="Dunes d'Erg Chegaga&#10;Kasbah Ait Ben Haddou&#10;Coucher de soleil"
                      />
                    </div>
                    <div>
                      <Label>Points forts (EN) - un élément par ligne</Label>
                      <Textarea
                        value={highlightsEn}
                        onChange={(e) => setHighlightsEn(e.target.value)}
                        rows={6}
                        placeholder="Erg Chegaga dunes&#10;Ait Ben Haddou Kasbah&#10;Sunset"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* TAB: Itinéraire */}
                <TabsContent value="itinerary" className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg">Programme jour par jour</Label>
                    <Button type="button" variant="outline" onClick={addItineraryDay}>
                      <Plus className="h-4 w-4 mr-2" /> Ajouter un jour
                    </Button>
                  </div>

                  {formData.itinerary.length === 0 ? (
                    <div className="text-center py-8 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground">Aucun jour ajouté</p>
                      <Button type="button" variant="outline" onClick={addItineraryDay} className="mt-2">
                        Ajouter le premier jour
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.itinerary.map((day, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-muted/20">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                              <Badge variant="secondary">Jour {day.day}</Badge>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItineraryDay(index)}
                              className="text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <Label className="text-sm">Titre du jour (FR)</Label>
                              <Input
                                value={day.title?.fr || ""}
                                onChange={(e) => updateItineraryDay(index, "title", "fr", e.target.value)}
                                placeholder="Marrakech - Ait Ben Haddou"
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Titre du jour (EN)</Label>
                              <Input
                                value={day.title?.en || ""}
                                onChange={(e) => updateItineraryDay(index, "title", "en", e.target.value)}
                                placeholder="Marrakech - Ait Ben Haddou"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm">Description (FR)</Label>
                              <Textarea
                                value={day.description?.fr || ""}
                                onChange={(e) => updateItineraryDay(index, "description", "fr", e.target.value)}
                                rows={3}
                                placeholder="Départ de Marrakech..."
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Description (EN)</Label>
                              <Textarea
                                value={day.description?.en || ""}
                                onChange={(e) => updateItineraryDay(index, "description", "en", e.target.value)}
                                rows={3}
                                placeholder="Departure from Marrakech..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* TAB: Médias */}
                <TabsContent value="media" className="space-y-4">
                  <ImageSelector
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Image principale *"
                  />

                  <Separator />

                  <div>
                    <Label>Galerie d'images (URLs) - une URL par ligne</Label>
                    <Textarea
                      value={galleryInput}
                      onChange={(e) => setGalleryInput(e.target.value)}
                      rows={6}
                      placeholder="https://exemple.com/image1.jpg&#10;https://exemple.com/image2.jpg"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Entrez les URLs des images supplémentaires, une par ligne
                    </p>
                  </div>
                </TabsContent>

                {/* TAB: Traductions */}
                <TabsContent value="translations" className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Traductions additionnelles (Arabe, Néerlandais, Espagnol)
                  </p>

                  <div className="space-y-4">
                    <div>
                      <Label>Titre (AR - Arabe)</Label>
                      <Input
                        value={formData.title.ar || ""}
                        onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                        dir="rtl"
                      />
                    </div>
                    <div>
                      <Label>Description (AR - Arabe)</Label>
                      <Textarea
                        value={formData.description.ar || ""}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, ar: e.target.value } })}
                        rows={3}
                        dir="rtl"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Titre (NL - Néerlandais)</Label>
                      <Input
                        value={formData.title.nl || ""}
                        onChange={(e) => setFormData({ ...formData, title: { ...formData.title, nl: e.target.value } })}
                      />
                    </div>
                    <div>
                      <Label>Titre (ES - Espagnol)</Label>
                      <Input
                        value={formData.title.es || ""}
                        onChange={(e) => setFormData({ ...formData, title: { ...formData.title, es: e.target.value } })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Description (NL - Néerlandais)</Label>
                      <Textarea
                        value={formData.description.nl || ""}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, nl: e.target.value } })}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Description (ES - Espagnol)</Label>
                      <Textarea
                        value={formData.description.es || ""}
                        onChange={(e) => setFormData({ ...formData, description: { ...formData.description, es: e.target.value } })}
                        rows={3}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingCircuit ? "Mettre à jour" : "Créer le circuit"}
                  </Button>
                </div>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {circuits.map((circuit) => (
          <Card key={circuit.id} className={`overflow-hidden ${!circuit.is_active ? "opacity-60" : ""}`}>
            <div className="relative h-48">
              <img 
                src={circuit.image_url} 
                alt="" 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = "https://via.placeholder.com/400x200?text=No+Image"; }}
              />
              <div className="absolute top-2 left-2 flex gap-2">
                <Badge className={circuit.type === "4x4" ? "bg-amber-600" : "bg-emerald-600"}>{circuit.type}</Badge>
                {!circuit.is_active && <Badge variant="secondary">Inactif</Badge>}
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-lg mb-1 line-clamp-1">{circuit.title?.fr || "Sans titre"}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {circuit.duration_days} jours • {circuit.departure_city} • €{circuit.price}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(circuit)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleToggleActive(circuit)}>
                  {circuit.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(circuit.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminCircuits;
